import { useCallback, useEffect, useMemo, useState } from "react";
import intl from "react-intl-universal";
import { Redirect } from "react-router-dom";
import { TbCopy, TbLink, TbRefresh, TbTrash } from "react-icons/tb";
import { PageHeaderWithBack } from "../../../../Components/Molecules";
import { useBaasInstance } from "../../../../Contexts/BaasInstanceContext";
import { useBaasCapabilities } from "../../../../Hooks/useBaasCapabilities";
import { useBaasPageLoading } from "../../../../Hooks/useBaasPageLoading";
import { useToast } from "../../../../Hooks/toast";
import { GridPagination } from "../../../../Components/Molecules/GridHistory";
import { paginateList } from "../../Extract/extractFilters";
import { fetchBaasInstanceFeatures } from "../../../../Services/baasInstancesApi";
import {
  createBaasKycOnboardInvite,
  listBaasKycOnboardInvites,
  revokeBaasKycOnboardInvite,
} from "../../../../Services/baasEntidadesApi";
import { BaasTablePageSkeleton } from "../components/BaasSkeleton";
import { BaasConfirmModal } from "../components/BaasConfirmModal";
import {
  EMPTY_BAAS_ONBOARD_FILTERS,
  ONBOARD_STATUS_BADGE_STYLES,
  ONBOARD_STATUS_LABEL_KEYS,
  buildInviteUrl,
  canRevokeOnboardInvite,
  filterBaasOnboardInvites,
  formatOnboardInviteDate,
  mapOnboardInvite,
  resolveOnboardProxyError,
} from "./baasOnboardInvites";
import { BaasOnboardFilterBar } from "./components/BaasOnboardFilterBar";
import { ENTIDADES_LIST_PATH } from "./BaasEntidadeCreatePage";

const KYC_INVITE_FEATURE = "kyc.invite_link";

export const BaasOnboardPage = () => {
  const { selectedInstance, dataEpoch } = useBaasInstance();
  const { can, loading: capabilitiesLoading } = useBaasCapabilities();
  const { addToast } = useToast();
  const canCreate = can("entities.create");
  const pageLoading = useBaasPageLoading(
    selectedInstance?.id ? `${selectedInstance.id}:${dataEpoch}` : null,
  );

  const [featureEnabled, setFeatureEnabled] = useState(false);
  const [featureLoading, setFeatureLoading] = useState(false);
  const [invites, setInvites] = useState([]);
  const [listLoading, setListLoading] = useState(false);
  const [creating, setCreating] = useState(false);
  const [revokingId, setRevokingId] = useState(null);
  const [copiedId, setCopiedId] = useState(null);
  const [filters, setFilters] = useState(EMPTY_BAAS_ONBOARD_FILTERS);
  const [page, setPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [revokeTarget, setRevokeTarget] = useState(null);
  const [revokeError, setRevokeError] = useState("");
  const [inviteName, setInviteName] = useState("");

  const canManage = canCreate && !capabilitiesLoading && Boolean(selectedInstance);

  const loadFeatures = useCallback(async () => {
    if (!selectedInstance?.baasInstanceId || !canManage) {
      setFeatureEnabled(false);
      return;
    }
    setFeatureLoading(true);
    try {
      const features = await fetchBaasInstanceFeatures(selectedInstance);
      const match = (Array.isArray(features) ? features : []).find(
        (item) =>
          String(item?.featureCode || "")
            .trim()
            .toLowerCase() === KYC_INVITE_FEATURE,
      );
      setFeatureEnabled(Boolean(match?.enabled));
    } catch {
      setFeatureEnabled(false);
    } finally {
      setFeatureLoading(false);
    }
  }, [selectedInstance, canManage]);

  const loadInvites = useCallback(async () => {
    if (!selectedInstance || !canManage || !featureEnabled) return;
    setListLoading(true);
    try {
      const rows = await listBaasKycOnboardInvites(selectedInstance);
      setInvites(rows.map(mapOnboardInvite));
    } catch (err) {
      addToast({
        type: "error",
        title: intl.get("baas.entidades.quickActions.onboardError"),
        description: resolveOnboardProxyError(err),
      });
    } finally {
      setListLoading(false);
    }
  }, [addToast, canManage, featureEnabled, selectedInstance]);

  useEffect(() => {
    loadFeatures();
  }, [loadFeatures]);

  useEffect(() => {
    if (featureEnabled) {
      loadInvites();
    } else {
      setInvites([]);
    }
  }, [featureEnabled, loadInvites]);

  const filteredItems = useMemo(
    () => filterBaasOnboardInvites(invites, filters),
    [invites, filters],
  );

  const paginatedItems = useMemo(
    () => paginateList(filteredItems, page, rowsPerPage),
    [filteredItems, page, rowsPerPage],
  );

  const handleFiltersChange = (nextFilters) => {
    setFilters(nextFilters);
    setPage(1);
  };

  const handleCreate = async () => {
    if (creating || !selectedInstance || !featureEnabled) return;
    setCreating(true);
    try {
      const raw = await createBaasKycOnboardInvite(selectedInstance, {
        expiresInHours: 168,
        name: inviteName,
      });
      const invite = mapOnboardInvite(raw);
      const url = buildInviteUrl(invite.path || invite.token);
      setInvites((prev) => [invite, ...prev.filter((row) => row.inviteId !== invite.inviteId)]);
      setInviteName("");
      try {
        await navigator.clipboard.writeText(url);
        setCopiedId(invite.inviteId);
        window.setTimeout(() => setCopiedId(null), 2000);
      } catch {
        /* ignore */
      }
      addToast({
        type: "success",
        title: intl.get("baas.entidades.quickActions.onboardCreateSuccess"),
      });
    } catch (err) {
      addToast({
        type: "error",
        title: intl.get("baas.entidades.quickActions.onboardError"),
        description: resolveOnboardProxyError(err),
      });
    } finally {
      setCreating(false);
    }
  };

  const handleCopy = async (invite) => {
    const url = buildInviteUrl(invite.path || invite.token);
    try {
      await navigator.clipboard.writeText(url);
      setCopiedId(invite.inviteId);
      window.setTimeout(() => setCopiedId(null), 2000);
    } catch {
      /* ignore */
    }
  };

  const handleRevokeConfirm = async () => {
    const invite = revokeTarget;
    if (!invite?.inviteId || revokingId || !selectedInstance) return;
    setRevokingId(invite.inviteId);
    setRevokeError("");
    try {
      const raw = await revokeBaasKycOnboardInvite(selectedInstance, invite.inviteId);
      const updated = mapOnboardInvite(raw);
      setInvites((prev) =>
        prev.map((row) => (row.inviteId === updated.inviteId ? updated : row)),
      );
      setRevokeTarget(null);
    } catch (err) {
      setRevokeError(resolveOnboardProxyError(err));
    } finally {
      setRevokingId(null);
    }
  };

  if (!capabilitiesLoading && !canCreate) {
    return <Redirect to="/baas/inicio" />;
  }

  if (pageLoading || capabilitiesLoading || featureLoading) {
    return <BaasTablePageSkeleton columns={6} />;
  }

  return (
    <div className="baas-onboard min-w-0 space-y-5">
      <PageHeaderWithBack
        title={intl.get("baas.entidades.quickActions.onboardModalTitle")}
        subTitle={
          selectedInstance?.name
            ? `${intl.get("baas.entidades.quickActions.onboardListHint")} — ${selectedInstance.name}`
            : intl.get("baas.entidades.quickActions.onboardListHint")
        }
        backTo={ENTIDADES_LIST_PATH}
        className="mb-0"
      />
      <header className="flex min-w-0 flex-wrap items-end justify-end gap-2">
        <label className="flex min-w-[12rem] max-w-xs flex-1 flex-col gap-1">
          <span className="text-xs font-medium text-gray-500 dark:text-gray-400">
            {intl.get("baas.entidades.quickActions.onboardInviteNameLabel")}
          </span>
          <input
            type="text"
            className="h-10 w-full rounded-lg border border-gray-200 px-3 text-sm text-brand-dark outline-none transition focus:border-[#e7a73f] focus:ring-2 focus:ring-[#e7a73f]/20 dark:border-brand-dark-border dark:bg-brand-dark-elevated dark:text-gray-100"
            value={inviteName}
            onChange={(e) => setInviteName(e.target.value)}
            placeholder={intl.get("baas.entidades.quickActions.onboardInviteNamePlaceholder")}
            maxLength={128}
            disabled={creating || !featureEnabled}
            autoComplete="off"
          />
        </label>
        <button
          type="button"
          className="btn-outline inline-flex w-auto shrink-0 items-center gap-1.5 !h-10 !px-3"
          onClick={loadInvites}
          disabled={listLoading || !featureEnabled}
        >
          <TbRefresh className={`h-4 w-4 ${listLoading ? "animate-spin" : ""}`} />
          {intl.get("baas.entidades.quickActions.onboardRefresh")}
        </button>
        <button
          type="button"
          className="btn-primary inline-flex w-auto shrink-0 items-center gap-2 !h-10 !px-4"
          onClick={() => {
            void handleCreate();
          }}
          disabled={creating || !featureEnabled}
        >
          <TbLink className="h-4 w-4" />
          {creating
            ? intl.get("baas.entidades.quickActions.onboardCreating")
            : intl.get("baas.entidades.quickActions.createOnboardLink")}
        </button>
      </header>

      {!featureEnabled ? (
        <p className="text-sm text-gray-500 dark:text-gray-400">
          {intl.get("baas.entidades.quickActions.onboardEnableHint")}
        </p>
      ) : null}

      <BaasOnboardFilterBar
        filters={filters}
        onFiltersChange={handleFiltersChange}
        filteredItems={filteredItems}
      />

      <div className="app-card overflow-hidden">
        <div className="overflow-x-auto">
          <table className="min-w-full text-left text-sm">
            <thead className="border-b border-gray-100 bg-gray-50/80 text-xs font-semibold uppercase tracking-wider text-gray-500 dark:border-brand-dark-border dark:bg-brand-dark-elevated/50 dark:text-gray-400">
              <tr>
                <th className="px-4 py-3">
                  {intl.get("baas.entidades.quickActions.onboardColumnStatus")}
                </th>
                <th className="px-4 py-3">
                  {intl.get("baas.entidades.quickActions.onboardColumnUrl")}
                </th>
                <th className="px-4 py-3">
                  {intl.get("baas.entidades.quickActions.onboardColumnEntity")}
                </th>
                <th className="px-4 py-3">
                  {intl.get("baas.entidades.quickActions.onboardColumnCreated")}
                </th>
                <th className="px-4 py-3">
                  {intl.get("baas.entidades.quickActions.onboardColumnExpires")}
                </th>
                <th className="px-4 py-3">
                  {intl.get("baas.entidades.quickActions.onboardColumnActions")}
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100 dark:divide-brand-dark-border">
              {listLoading && invites.length === 0 ? (
                <tr>
                  <td colSpan={6} className="px-4 py-8 text-center text-sm text-gray-500">
                    {intl.get("baas.entidades.detail.verifications.inviteLoading")}
                  </td>
                </tr>
              ) : paginatedItems.length === 0 ? (
                <tr>
                  <td colSpan={6} className="px-4 py-8 text-center text-sm text-gray-500">
                    {intl.get("baas.entidades.quickActions.onboardEmpty")}
                  </td>
                </tr>
              ) : (
                paginatedItems.map((invite) => {
                  const statusKey =
                    ONBOARD_STATUS_LABEL_KEYS[invite.status] || ONBOARD_STATUS_LABEL_KEYS.pending;
                  const badge =
                    ONBOARD_STATUS_BADGE_STYLES[invite.status] || ONBOARD_STATUS_BADGE_STYLES.pending;
                  const url = buildInviteUrl(invite.path || invite.token);
                  return (
                    <tr key={invite.inviteId || invite.token}>
                      <td className="px-4 py-3">
                        <span
                          className={`inline-flex rounded-full px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide ${badge}`}
                        >
                          {intl.get(statusKey) || invite.status}
                        </span>
                      </td>
                      <td className="max-w-[18rem] px-4 py-3">
                        <p className="break-all font-mono text-xs text-gray-600 dark:text-gray-300">
                          {url}
                        </p>
                      </td>
                      <td className="px-4 py-3">
                        {invite.entityId ? (
                          <span className="font-mono text-xs text-gray-500">{invite.entityId}</span>
                        ) : invite.pendingName ? (
                          <span className="text-xs text-gray-700 dark:text-gray-200">
                            {invite.pendingName}
                          </span>
                        ) : (
                          <span className="text-xs text-amber-700 dark:text-amber-400">
                            {intl.get("baas.entidades.quickActions.onboardAwaitingPerson")}
                          </span>
                        )}
                      </td>
                      <td className="whitespace-nowrap px-4 py-3 text-xs text-gray-500">
                        {formatOnboardInviteDate(invite.createdAt)}
                      </td>
                      <td className="whitespace-nowrap px-4 py-3 text-xs text-gray-500">
                        {formatOnboardInviteDate(invite.expiresAt)}
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex flex-wrap gap-2">
                          <button
                            type="button"
                            onClick={() => {
                              void handleCopy(invite);
                            }}
                            className="inline-flex items-center gap-1 rounded-md border border-gray-200 px-2 py-1 text-xs dark:border-brand-dark-border"
                          >
                            <TbCopy className="h-3.5 w-3.5" />
                            {copiedId === invite.inviteId
                              ? intl.get("baas.entidades.detail.verifications.copied")
                              : intl.get("baas.entidades.detail.verifications.copy")}
                          </button>
                          {canRevokeOnboardInvite(invite) ? (
                            <button
                              type="button"
                              disabled={revokingId === invite.inviteId}
                              onClick={() => {
                                setRevokeError("");
                                setRevokeTarget(invite);
                              }}
                              className="inline-flex items-center gap-1 rounded-md border border-red-200 px-2 py-1 text-xs text-red-700 dark:border-red-900 dark:text-red-400 disabled:opacity-50"
                            >
                              <TbTrash className="h-3.5 w-3.5" />
                              {intl.get("baas.entidades.detail.verifications.inviteRevoke")}
                            </button>
                          ) : null}
                        </div>
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>

        <GridPagination
          total={filteredItems.length}
          page={page}
          rowsPerPage={rowsPerPage}
          onPageChange={setPage}
          onRowsPerPageChange={(value) => {
            setRowsPerPage(value);
            setPage(1);
          }}
          currentPageCount={paginatedItems.length}
          itemLabel={intl.get("baas.entidades.quickActions.onboardPaginationLabel")}
        />
      </div>

      <BaasConfirmModal
        open={Boolean(revokeTarget)}
        onClose={() => {
          if (revokingId) return;
          setRevokeTarget(null);
          setRevokeError("");
        }}
        onConfirm={handleRevokeConfirm}
        title={intl.get("baas.entidades.quickActions.onboardRevokeTitle")}
        message={intl.get("baas.entidades.quickActions.onboardRevokeConfirm")}
        confirmLabel={intl.get("baas.entidades.detail.verifications.inviteRevoke")}
        busyLabel={intl.get("baas.entidades.quickActions.onboardRevoking")}
        busy={Boolean(revokingId)}
        error={revokeError}
      />
    </div>
  );
};
